/**
 * Slice 23 — the theme controller. The single runtime authority for which
 * palette is live, how it persists, and who gets told when it changes.
 *
 * 🔴 FIRST PAINT IS NOT THIS FILE'S JOB. The inline `<script>` in index.html
 * runs before the stylesheet and before this module loads; it reads the stored
 * preference and stamps `data-theme` + the `theme-color` meta so the FIRST
 * painted frame is already correct. This module owns everything AFTER that: the
 * live `auto` follow, the Settings toggle, and the change event the map layers
 * repaint on. The two must agree on the key, the valid values, the resolve rule
 * and the two `theme-color` values — the inline script is the minimal mirror of
 * exactly those four facts and nothing else.
 *
 * 🔴 WHY AN EVENT AND NOT JUST REACT STATE. MapLibre and the tile-free canvas
 * hold their OWN copy of every colour (a paint expression resolved to a literal,
 * a canvas stroke) — a React re-render does not reach them. They subscribe to
 * `THEME_EVENT` and repaint. Persistence is per-device localStorage, not
 * server-side: theme has no server consumer, and a pre-paint decision cannot
 * wait for a round-trip.
 */

export const THEME_KEY = 'raad-theme';
export const THEME_EVENT = 'raad-theme-change';

export type ThemePref = 'light' | 'dark' | 'auto';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_PREFS: readonly ThemePref[] = ['light', 'dark', 'auto'] as const;

/** The default when nothing is stored. REBRAND 28 Oct 2026 (Mahad) — LIGHT is now
 *  the web dashboard's default. A user with a stored preference keeps it (dark stays
 *  a full, first-class option); only a visitor with no stored pref lands on light.
 *  `resolveTheme`/`applyResolved` STAMP `data-theme='light'` for the default, and the
 *  inline pre-paint script in index.html mirrors the same rule (dark only when
 *  explicitly stored, or `auto` on a dark OS). */
export const DEFAULT_PREF: ThemePref = 'light';

/** The `theme-color` meta / browser-chrome colour per resolved theme — the page
 *  background token's value in each palette (`--raad-bg`). Kept in lockstep with
 *  styles.css and the inline pre-paint script. */
const THEME_COLOR: Record<ResolvedTheme, string> = {
  dark: '#0E1216',
  light: '#f3f4f1',
};

function isThemePref(v: string | null): v is ThemePref {
  return v === 'light' || v === 'dark' || v === 'auto';
}

/** The stored preference, validated; anything unexpected (or no storage at all)
 *  falls back to the default (light) rather than throwing. */
export function readThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (isThemePref(v)) return v;
  } catch {
    /* private mode / storage disabled — fall through to the default */
  }
  return DEFAULT_PREF;
}

function media(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia('(prefers-color-scheme: light)');
}

/** Resolve a preference to a concrete palette. `auto` reads the OS preference
 *  live; everything else is itself. */
export function resolveTheme(pref: ThemePref): ResolvedTheme {
  if (pref === 'auto') return media()?.matches ? 'light' : 'dark';
  return pref;
}

/** Stamp the resolved palette onto the document and the browser chrome. `dark`
 *  clears the attribute (dark is the bare `:root`), so the DOM matches what the
 *  pre-paint script leaves behind and no stale `data-theme` lingers. */
function applyResolved(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  if (resolved === 'light') root.setAttribute('data-theme', 'light');
  else root.removeAttribute('data-theme');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR[resolved]);
}

/** Apply a resolved theme and notify subscribers. Fired on every explicit choice
 *  and every `auto` boundary crossing; the map's repaint handler re-reads tokens
 *  and is idempotent, so an event that lands on an unchanged palette is harmless,
 *  and React's pref subscription still needs it (choosing `auto` on a dark OS
 *  changes the PREFERENCE without changing the resolved palette). */
function commit(resolved: ResolvedTheme): void {
  applyResolved(resolved);
  window.dispatchEvent(new CustomEvent<ResolvedTheme>(THEME_EVENT, { detail: resolved }));
}

/** Persist a new preference and apply it. Returns the resolved palette. */
export function setThemePref(pref: ThemePref): ResolvedTheme {
  try {
    localStorage.setItem(THEME_KEY, pref);
  } catch {
    /* storage disabled — the choice still applies for this session */
  }
  const resolved = resolveTheme(pref);
  commit(resolved);
  return resolved;
}

/** The resolved palette right now, from the stored preference. */
export function currentResolved(): ResolvedTheme {
  return resolveTheme(readThemePref());
}

let initialised = false;

/**
 * Wire the live `auto` follow, once, at app startup. The pre-paint script has
 * already applied first paint, so this only records the baseline and starts
 * tracking the OS preference — when the stored preference is `auto`, an OS
 * light/dark flip (dusk) re-commits and repaints without a reload.
 */
export function initTheme(): void {
  applyResolved(currentResolved());
  if (initialised) return;
  initialised = true;

  const mq = media();
  if (!mq) return;
  // 🔴 THE AUTO-FOLLOW LISTENER LIVES FOR THE APP'S LIFETIME AND IS DELIBERATELY
  // NOT REMOVABLE. Dusk must switch an `auto` user's app whoever happens to be
  // mounted, so no disposer is returned — neither caller (main.tsx at import,
  // useTheme on mount) can tear it down. Returning a destructive disposer here
  // would let the first component unmount silently kill the follow while
  // `initialised` stays true, so every later mount no-ops. Not returning one at
  // all removes that whole class of trap.
  mq.addEventListener('change', () => {
    if (readThemePref() === 'auto') commit(resolveTheme('auto'));
  });
}
